import { DeviceClass } from "model/device";

function parseBoolean(value) {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true") return true;
        if (normalized === "false") return false;
    }
    return null;
}

function isPeakShavingEligibleBatteryDevice(device) {
    if (device?.device_class !== DeviceClass.BATTERY) {
        return false;
    }

    const batteryTrackingEnabled = parseBoolean(device?.battery_tracking_enabled) === true;
    const batteryCapacity = Number(device?.capacity);
    const hasPositiveCapacity = Number.isFinite(batteryCapacity) && batteryCapacity > 0;

    return batteryTrackingEnabled && hasPositiveCapacity;
}

// Walks the schematic tree so callers can also read the node's live metering (e.g. soc) by id.
export function findPeakShavingEligibleBatteryNode(siteData) {
    const rootNode = siteData?.devices_general?.schematic;
    if (!rootNode || !siteData?.devices) {
        return null;
    }

    function traverse(node) {
        if (!node) return null;
        if (node.device_id && isPeakShavingEligibleBatteryDevice(siteData.devices[node.device_id])) {
            return node;
        }
        for (const child of node.children ?? []) {
            const found = traverse(child);
            if (found) return found;
        }
        return null;
    }

    return traverse(rootNode);
}

export function hasPeakShavingEligibleBattery(siteData) {
    return findPeakShavingEligibleBatteryNode(siteData) !== null;
}

export function getPeakShavingStatus(siteData) {
    const enabled = !!(siteData?.config?.peak_shaving_offtake ?? siteData?.config?.peak_shaving_enabled);
    const hasEligibleBattery = hasPeakShavingEligibleBattery(siteData);

    return {
        enabled,
        hasEligibleBattery,
        effectivelyActive: enabled && hasEligibleBattery,
    };
}

export function hasValue(value) {
    if (value === null || value === undefined) {
        return false;
    }
    return typeof value === "string" ? value.trim() !== "" : true;
}

export function parseBound(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
}

export function resolveSocLimits(minimumSoc, maximumSoc) {
    const minimumSocBound = parseBound(minimumSoc);
    const maximumSocBound = parseBound(maximumSoc);
    const hasCustomSocBounds = minimumSocBound !== null && maximumSocBound !== null;

    return {
        minSocLimit: hasCustomSocBounds ? minimumSocBound : 0,
        maxSocLimit: hasCustomSocBounds ? maximumSocBound : 100
    };
}

export function isValidPeakShavingMinSoc(value, minSocLimit, maxSocLimit) {
    if (!hasValue(value)) {
        return false;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= minSocLimit && parsed <= maxSocLimit;
}
